import React, {
	useCallback,
	useRef,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
	type FocusEventHandler,
	type FocusEvent,
} from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import isNil from 'lodash/fp/isNil';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import Form, { ErrorMessage, Field, FormSection, useFormState } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Inline, xcss, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Select, {
	type ValueType,
	type OptionProps,
	type SingleValueProps,
	type ControlProps,
	type ActionMeta,
	type InputActionMeta,
	components,
} from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getDefaultTimes } from '@atlassian/eoc-day-time-interval';
import { layers } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useLocale } from '@atlassian/jira-tenant-context-controller';
import Ipcontext from '../../services/context';
import { useIncrementCreationService } from '../../services/create-increment';
import { useFetchCustomFieldValues } from '../../services/fetch-custom-field-values';
import { useFetchCustomFields } from '../../services/fetch-custom-fields';
import { useFetchIssueCount } from '../../services/fetch-issue-count';
import messages from './messages';
import type { BoardScopeFilterOptionType, FormData, Props, Option, Project } from './types';
import { getNextQuarterStartDate, getWeekStartDay } from './utils';

const ITERATION_LENGTH = 2;
const ITERATIONS_COUNT = 6;

const refetchIssueCount = (
	startDate: string | undefined,
	issueCountVisible: boolean,
	filterFieldId: string | undefined,
	filterFieldValue: string | undefined,
	lastStartDate: React.MutableRefObject<string | undefined>,
	lastFilterFieldValue: React.MutableRefObject<string | undefined>,
) => {
	// startDate is always required
	if (!startDate) {
		return false;
	}

	const filterIsValid = !filterFieldId || (filterFieldId && filterFieldValue);
	const countEmptyOrValuesChanged =
		!issueCountVisible ||
		lastStartDate.current !== startDate ||
		(!!filterFieldId && !!filterFieldValue && lastFilterFieldValue.current !== filterFieldValue);

	if (
		// first off run. checking filterFieldId and filterFieldValue as otherwise this will be true in case where the user selects those two and then clears the filterFieldValue - we don't want to call the endpoint with empty filterFieldValue
		!fg('fix_ip_checking_issue_count_without_filter_value') &&
		((!issueCountVisible && !filterFieldId && !filterFieldValue) ||
			lastStartDate.current !== startDate ||
			(filterFieldId && filterFieldValue && lastFilterFieldValue.current !== filterFieldValue))
	) {
		return true;
	}

	if (
		// Fetch field values if count is empty (initial state) or values is changed, and the filter field and values are valid

		countEmptyOrValuesChanged &&
		filterIsValid &&
		fg('fix_ip_checking_issue_count_without_filter_value')
	) {
		return true;
	}
	if (fg('fix_ip_checking_issue_count_without_filter_value')) {
		return false;
	}
};

const IssueCount = ({ initialStartDate }: { initialStartDate: string }) => {
	const { formatMessage } = useIntl();
	const { planId, scenarioId } = useContext(Ipcontext);
	const formState = useFormState();

	const [issueCountVisible, setIssueCountVisible] = useState(false);

	const startDate = formState?.values?.start;
	const filterFieldId = formState?.values?.filterFieldId?.value;
	const filterFieldValue = formState?.values?.filterFieldValue?.value;

	const lastStartDate = useRef<string | undefined>(initialStartDate);
	const lastFilterField = useRef<string | undefined>(undefined);
	const lastFilterFieldValue = useRef<string | undefined>(undefined);

	const {
		issueCount,
		fetchIssueCount,
		isLoading: isLoadingIssueCount,
	} = useFetchIssueCount({
		planId,
		scenarioId,
		iterationsCount: ITERATIONS_COUNT,
		iterationLength: ITERATION_LENGTH,
		start: new Date(startDate).getTime(),
		filterFieldId,
		filterFieldValue,
	});

	useEffect(() => {
		if (
			refetchIssueCount(
				startDate,
				issueCountVisible,
				filterFieldId,
				filterFieldValue,
				lastStartDate,
				lastFilterFieldValue,
			)
		) {
			fetchIssueCount();
			setIssueCountVisible(true);

			if (lastStartDate.current !== startDate) {
				lastStartDate.current = startDate;
			}
			if (lastFilterField.current !== filterFieldId) {
				lastFilterField.current = filterFieldId;
			}
			if (lastFilterFieldValue.current !== filterFieldValue) {
				lastFilterFieldValue.current = filterFieldValue;
			}
		}
	}, [
		fetchIssueCount,
		filterFieldId,
		lastFilterField,
		filterFieldValue,
		lastFilterFieldValue,
		issueCountVisible,
		lastStartDate,
		startDate,
	]);

	// hide issue count if any values were cleared, otherwise the count is not accurate
	useEffect(() => {
		if (
			!startDate ||
			// check if it was fetched and then cleared for the below two fields
			(!filterFieldId && lastFilterField.current) ||
			(!filterFieldValue && lastFilterFieldValue.current)
		) {
			setIssueCountVisible(false);
		}
	}, [startDate, filterFieldId, lastFilterField, filterFieldValue, lastFilterFieldValue]);

	return (
		<IssueCountText>
			{formatMessage(messages.issueCount)}{' '}
			{isLoadingIssueCount ? (
				<Spinner size="small" />
			) : (
				<Text as="strong">{issueCountVisible ? issueCount : '-'}</Text>
			)}
		</IssueCountText>
	);
};

const OptionComponent = ({ project, label }: { project?: Project; label: string }) => {
	const projectElements = !isNil(project) && (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<span>&nbsp;(</span>
			<Avatar appearance="square" size="xsmall" name={project.key} src={project.avatar} />
			<span>&nbsp;{project.key})</span>
		</>
	);
	return (
		<Inline alignBlock="center">
			<Box
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				xcss={xcss({
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				})}
			>
				{label}
			</Box>
			{projectElements}
		</Inline>
	);
};

const BoardScopeFilterControl = ({
	children,
	...props
}: ControlProps<BoardScopeFilterOptionType>) => {
	const selectedValue = props.getValue();
	return (
		<Tooltip content={selectedValue.length > 0 && selectedValue[0].label}>
			<components.Control {...props}>{children}</components.Control>
		</Tooltip>
	);
};

const BoardScopeFilterSingleValue = (props: SingleValueProps<BoardScopeFilterOptionType>) => (
	<components.SingleValue {...props}>
		<OptionComponent label={props.data.label} project={props.data.project} />
	</components.SingleValue>
);
const BoardScopeFilterOption = (props: OptionProps<BoardScopeFilterOptionType>) => (
	<Tooltip content={props.data.label}>
		<components.Option {...props}>
			<OptionComponent label={props.data.label} project={props.data.project} />
		</components.Option>
	</Tooltip>
);

const ScopeFilterValue = () => {
	const { formatMessage } = useIntl();
	const { planId, scenarioId } = useContext(Ipcontext);
	const formState = useFormState();
	const previousFieldId = useRef<string | undefined>(undefined);
	const fieldId: string | undefined = formState?.values?.filterFieldId?.value;
	const {
		values: customFieldValues,
		isLoading,
		fetchCustomFieldValues,
	} = useFetchCustomFieldValues({
		planId,
		scenarioId,
		fieldId,
	});
	const currentFieldValue = formState?.values.filterFieldValue;

	const debouncedLoadOptions = useMemo(
		() =>
			debounce((query: string) => {
				fetchCustomFieldValues(query);
			}, 200),
		[fetchCustomFieldValues],
	);

	const customFieldValueOptions = useMemo(
		() =>
			(customFieldValues || []).map((v) => ({
				label: v.value,
				value: v.value,
			})),
		[customFieldValues],
	);

	const valueOfSelect = useMemo(
		() => customFieldValueOptions.find((v) => v.value === currentFieldValue?.value),
		[customFieldValueOptions, currentFieldValue],
	);

	const onInputChangeHandler = useCallback(
		(query: string, actionMeta: InputActionMeta) => {
			if (actionMeta.action !== 'input-change') {
				return;
			}
			debouncedLoadOptions(query);
		},
		[debouncedLoadOptions],
	);

	const onChangeHandler = useCallback(
		(
			newValue: Option | null,
			actionMeta: ActionMeta,
			onChange: (newValue: Option | null, actionMeta: ActionMeta) => void,
		) => {
			if (actionMeta.action === 'clear') {
				debouncedLoadOptions('');
			}

			onChange(newValue, actionMeta);
		},
		[debouncedLoadOptions],
	);

	const onFocusHandler = useCallback(
		(event: FocusEvent, onFocus: FocusEventHandler) => {
			if (isNil(valueOfSelect)) {
				debouncedLoadOptions('');
				onFocus(event);
			}
		},
		[debouncedLoadOptions, valueOfSelect],
	);

	useEffect(() => {
		if (previousFieldId.current !== fieldId) {
			if (!isNil(fieldId)) {
				debouncedLoadOptions('');
			}
			previousFieldId.current = fieldId;
		}
	}, [fieldId, debouncedLoadOptions]);

	return (
		<Box xcss={FieldBoxStyles}>
			<Field<ValueType<Option>>
				aria-required
				name="filterFieldValue"
				testId="portfolio-3-plan-increment-common.ui.increment-creation.custom-field-value"
				label={formatMessage(
					fg('fix_ip_checking_issue_count_without_filter_value')
						? messages.scopeFilterFieldValueLabel
						: messages.customFieldSettingFieldValueLabel,
				)}
				isDisabled={isNil(fieldId)}
			>
				{({ fieldProps: { id, value, onChange, onFocus, ...rest }, error }) => (
					<>
						<Select<Option>
							isLoading={isLoading}
							isClearable
							inputId={id}
							closeMenuOnScroll
							openMenuOnFocus
							options={customFieldValueOptions}
							onInputChange={onInputChangeHandler}
							value={valueOfSelect}
							styles={{
								menuPortal: (base) => ({
									...base,

									zIndex: layers.modal,
								}),
							}}
							menuPortalTarget={document.body}
							placeholder={formatMessage(
								fg('fix_ip_checking_issue_count_without_filter_value')
									? messages.scopeFilterFieldValuePlaceholder
									: messages.customFieldSettingFieldValuePlaceholder,
							)}
							controlShouldRenderValue={!isNil(valueOfSelect)}
							components={{
								...(isNil(valueOfSelect) && {
									ClearIndicator: () => null,
								}),
							}}
							aria-label={formatMessage(
								fg('fix_ip_checking_issue_count_without_filter_value')
									? messages.scopeFilterFieldValueLabel
									: messages.customFieldSettingFieldValueLabel,
							)}
							onChange={(newValue, actionMeta) => onChangeHandler(newValue, actionMeta, onChange)}
							onFocus={(event) => onFocusHandler(event, onFocus)}
							filterOption={() => true}
							{...rest}
						/>
						{error && <ErrorMessage>{error}</ErrorMessage>}
					</>
				)}
			</Field>
		</Box>
	);
};

const CustomFieldValueOld = () => {
	const { formatMessage } = useIntl();
	const { planId, scenarioId } = useContext(Ipcontext);
	const formState = useFormState();

	const fieldId: string | undefined = formState?.values?.filterFieldId?.value;
	const currentFieldValue = formState?.values.filterFieldValue;
	const { values: customFieldValues, isLoading } = useFetchCustomFieldValues({
		planId,
		scenarioId,
		fieldId,
	});

	const customFieldValueOptions = useMemo(
		() =>
			(customFieldValues || []).map((v) => ({
				label: v.value,
				value: v.value,
			})),
		[customFieldValues],
	);
	const isDisabled = isNil(fieldId) || isLoading;
	const valueOfSelect = useMemo(
		() => customFieldValueOptions.find((v) => v === currentFieldValue),
		[customFieldValueOptions, currentFieldValue],
	);
	return (
		<Box xcss={FieldBoxStyles}>
			<Field<ValueType<Option>>
				aria-required
				name="filterFieldValue"
				testId="portfolio-3-plan-increment-common.ui.increment-creation.custom-field-value"
				label={formatMessage(
					fg('fix_ip_checking_issue_count_without_filter_value')
						? messages.scopeFilterFieldValueLabel
						: messages.customFieldSettingFieldValueLabel,
				)}
				isDisabled={isDisabled}
				defaultValue={valueOfSelect}
			>
				{({ fieldProps: { id, value, ...rest }, error }) => (
					<>
						<Select<Option>
							isLoading={isLoading}
							isClearable
							inputId={id}
							closeMenuOnScroll
							styles={{
								menuPortal: (base) => ({
									...base,

									zIndex: layers.modal,
								}),
							}}
							menuPortalTarget={document.body}
							placeholder={formatMessage(
								fg('fix_ip_checking_issue_count_without_filter_value')
									? messages.scopeFilterFieldValuePlaceholder
									: messages.customFieldSettingFieldValuePlaceholder,
							)}
							options={customFieldValueOptions}
							value={valueOfSelect}
							controlShouldRenderValue={!isNil(currentFieldValue)}
							components={{
								...(isNil(valueOfSelect) && {
									ClearIndicator: () => null,
								}),
							}}
							aria-label={formatMessage(
								fg('fix_ip_checking_issue_count_without_filter_value')
									? messages.scopeFilterFieldValueLabel
									: messages.customFieldSettingFieldValueLabel,
							)}
							{...rest}
						/>
						{error && <ErrorMessage>{error}</ErrorMessage>}
					</>
				)}
			</Field>
		</Box>
	);
};

const IncrementCreation = ({ onIncrementCreated, isOpen = false, onClose }: Props) => {
	const { planId } = useContext(Ipcontext);
	const {
		customFields,
		fetchCustomFields,
		isLoading: isLoadingCustomFields,
	} = useFetchCustomFields({ planId });

	const customFieldOptions = useMemo(() => {
		if (isLoadingCustomFields) {
			return [];
		}
		return (customFields || []).map((f) => ({
			label: f.title,
			value: f.id,
			project: f.project,
		}));
	}, [customFields, isLoadingCustomFields]);

	const { saving, error, save } = useIncrementCreationService();
	const { formatMessage } = useIntl();
	const locale = useLocale();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCancelButtonClick = useCallback(() => {
		onClose();
	}, [onClose]);

	const focusRef = useRef<HTMLElement>();

	const getStartDate = useCallback((): string => getNextQuarterStartDate(new Date()), []);

	const handleSubmitForm = useCallback(
		async (data: FormData) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const errorMessages: any = {};
			if ((data.name?.length || 0) < 1) {
				errorMessages.name = formatMessage(messages.nameEmptyError);
			}
			if ((data.start?.length || 0) < 1) {
				errorMessages.start = formatMessage(messages.startDateEmptyError);
			}
			if (!isNil(data.filterFieldId) && isNil(data.filterFieldValue)) {
				errorMessages.filterFieldValue = formatMessage(messages.fieldValueEmptyError);
			}
			if (isNil(data.name) || isNil(data.start) || Object.keys(errorMessages).length > 0) {
				return errorMessages;
			}

			const newBoard = {
				name: data.name,
				start: new Date(data.start).getTime(),
				iterationsCount: ITERATIONS_COUNT,
				iterationLength: ITERATION_LENGTH,
				filterFieldId: data.filterFieldId?.value,
				filterFieldValue: data.filterFieldValue?.value,
			};
			const result = await save(newBoard);
			if (result !== undefined) {
				onIncrementCreated(result);
				if (ff('com.atlassian.rm.jpo.jpo3cloud.increment-planning-board-m1')) {
					fireUIAnalytics(createAnalyticsEvent({ action: 'added', actionSubject: 'programBoard' }));
				}
				onClose();
			}
		},
		[save, onIncrementCreated, onClose, formatMessage, createAnalyticsEvent],
	);

	useEffect(() => {
		if (isOpen) {
			fetchCustomFields();
		}
	}, [fetchCustomFields, isOpen]);

	const scopeFilterDescription = formatMessage(
		fg('fix_ip_checking_issue_count_without_filter_value')
			? messages.scopeFilterDescription
			: messages.customFieldSettingDescription,
		{
			bold: (chunks: ReactNode) => <b>{chunks}</b>,
		},
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={handleCancelButtonClick} autoFocus={focusRef} shouldReturnFocus>
					<ModalHeader>
						<div>
							<ModalTitle>{formatMessage(messages.addIncrementTitle)}</ModalTitle>
							{fg('replace_typography_css_with_tokens_arj') ? (
								<Box xcss={paragraphStyles}>
									<Text as="p">{formatMessage(messages.addIncrementDescription)}</Text>
								</Box>
							) : (
								<p>{formatMessage(messages.addIncrementDescription)}</p>
							)}
						</div>
						<ModalClose>
							<Button appearance="link" spacing="none" onClick={handleCancelButtonClick}>
								<CrossIcon
									label={formatMessage(messages.closeModal)}
									primaryColor={token('color.text.subtle', N500)}
								/>
							</Button>
						</ModalClose>
					</ModalHeader>

					<Form<FormData> onSubmit={handleSubmitForm}>
						{({ formProps, submitting }) => (
							<FormStyled {...formProps}>
								<ModalBody>
									<Field
										aria-required
										name="name"
										testId="portfolio-3-plan-increment-common.ui.increment-creation.name-field"
										label={formatMessage(messages.name)}
										defaultValue=""
									>
										{
											// eslint-disable-next-line @typescript-eslint/no-shadow
											({ fieldProps, error, valid }) => (
												<>
													<TextField
														autoComplete="off"
														placeholder={formatMessage(messages.namePlaceholder)}
														maxLength={255}
														{...fieldProps}
														ref={focusRef}
													/>
													{valid ||
														(error && (
															<ErrorMessage testId="portfolio-3-plan-increment-common.ui.increment-creation.user-submission-error">
																{error}
															</ErrorMessage>
														))}
												</>
											)
										}
									</Field>
									<Field
										aria-required
										name="start"
										testId="portfolio-3-plan-increment-common.ui.increment-creation.start-date-field"
										label={formatMessage(messages.startDate)}
										defaultValue={getStartDate()}
									>
										{
											// eslint-disable-next-line @typescript-eslint/no-shadow
											({ fieldProps, error }) => (
												<>
													<DateTimePicker
														{...fieldProps}
														locale={locale}
														datePickerProps={{
															weekStartDay: getWeekStartDay(locale),
														}}
														timePickerProps={{
															times: getDefaultTimes(),
														}}
														testId="portfolio-3-plan-increment-common.ui.increment-creation.start-date-picker"
													/>
													{error && <ErrorMessage>{error}</ErrorMessage>}
												</>
											)
										}
									</Field>

									<FormSection>
										<Heading size="medium">
											{formatMessage(
												fg('fix_ip_checking_issue_count_without_filter_value')
													? messages.scopeFilterHeading
													: messages.customFieldSettingHeading,
											)}
										</Heading>

										{fg('replace_typography_css_with_tokens_arj') ? (
											<Box xcss={paragraphStyles}>
												<Text as="p">{scopeFilterDescription}</Text>
											</Box>
										) : (
											<p>{scopeFilterDescription}</p>
										)}
										<Inline spread="space-between" space="space.100">
											<Box xcss={FieldBoxStyles}>
												<Field<ValueType<Option>>
													aria-required
													name="filterFieldId"
													testId="portfolio-3-plan-increment-common.ui.increment-creation.custom-field"
													label={formatMessage(
														fg('fix_ip_checking_issue_count_without_filter_value')
															? messages.scopeFilterFieldLabel
															: messages.customFieldSettingFieldLabel,
													)}
												>
													{({
														fieldProps: { id, ...rest },
														// eslint-disable-next-line @typescript-eslint/no-shadow
														error,
													}) => (
														<>
															<Select<Option>
																inputId={id}
																isLoading={isLoadingCustomFields}
																isClearable
																isSearchable
																closeMenuOnScroll
																styles={{
																	menuPortal: (base) => ({
																		...base,

																		zIndex: layers.modal,
																	}),
																}}
																menuPortalTarget={document.body}
																placeholder={formatMessage(
																	fg('fix_ip_checking_issue_count_without_filter_value')
																		? messages.scopeFilterFieldPlaceholder
																		: messages.customFieldSettingFieldPlaceholder,
																)}
																options={customFieldOptions}
																components={{
																	Option: BoardScopeFilterOption,
																	SingleValue: BoardScopeFilterSingleValue,
																	Control: BoardScopeFilterControl,
																}}
																{...rest}
															/>
															{error && <ErrorMessage>{error}</ErrorMessage>}
														</>
													)}
												</Field>
											</Box>
											{ff('com.atlassian.rm.jpo.jpo3cloud.increment-planning-board-m1') ? (
												<ScopeFilterValue />
											) : (
												<CustomFieldValueOld />
											)}
										</Inline>
									</FormSection>

									{!isNil(error) && (
										<FormSection>
											<SectionMessage appearance="error">
												{fg('replace_typography_css_with_tokens_arj') ? (
													<Box xcss={paragraphStyles}>
														<Text as="p">{formatMessage(messages.errorLabel)}</Text>
													</Box>
												) : (
													<p>{formatMessage(messages.errorLabel)}</p>
												)}
											</SectionMessage>
										</FormSection>
									)}
								</ModalBody>
								<ModalFooter>
									{ff('com.atlassian.rm.jpo.jpo3cloud.increment-planning-board-m1') && (
										<IssueCount initialStartDate={getStartDate()} />
									)}
									<ButtonGroup>
										<Button
											appearance="subtle"
											testId="portfolio-3-plan-increment-common.ui.increment-creation.cancel-button"
											onClick={handleCancelButtonClick}
											isDisabled={saving || submitting}
										>
											{formatMessage(messages.cancel)}
										</Button>
										<Button
											type="submit"
											testId="portfolio-3-plan-increment-common.ui.increment-creation.submit-button"
											appearance="primary"
											isDisabled={saving || submitting}
										>
											{formatMessage(messages.submit)}
										</Button>
									</ButtonGroup>
								</ModalFooter>
							</FormStyled>
						)}
					</Form>
				</Modal>
			)}
		</ModalTransition>
	);
};

export default IncrementCreation;

const IssueCountText = ({ children }: { children: ReactNode }) => (
	<Box xcss={issueCountStyles}>
		<Text as="p">{children}</Text>
	</Box>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalClose = styled.div({
	alignSelf: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormStyled = styled.form({
	marginTop: 0,
});

const issueCountStyles = xcss({
	marginRight: 'auto',
});

const FieldBoxStyles = xcss({
	width: '50%',
});

const paragraphStyles = xcss({
	marginTop: 'space.150',
});
